/**
 * useEstimatedProgress
 *
 * Drives a smooth progress bar from 0% toward a `ceiling` (default 95%) over
 * `expectedSeconds`. Used when the backend offers no real progress signal
 * (e.g. RunPod runsync) but we still want the UI to feel alive.
 *
 * Curve: 1 - exp(-t/tau) with tau = expectedSeconds / 2. At t = expected we
 * reach ~86% of the ceiling; at t = 2×expected we asymptote to the ceiling.
 * The curve never reaches 100% on its own — callers snap to 100 when the
 * real result arrives.
 *
 * When the backend *does* publish a real progress number, it takes over
 * (only when higher than our estimate — we never let it regress).
 */

import { useEffect, useRef, useState } from 'react';

const POLL_INTERVAL_MS = 250;
const DEFAULT_CEILING = 95;

interface UseEstimatedProgressInput {
  /** Whether the underlying job is in progress. */
  isActive: boolean;
  /** Expected wall-clock seconds for the job. */
  expectedSeconds: number;
  /** Backend-reported progress (0-100) when available. */
  backendProgress?: number | null;
  /** Cap before final snap (default 95). */
  ceiling?: number;
}

export function useEstimatedProgress({
  isActive,
  expectedSeconds,
  backendProgress,
  ceiling = DEFAULT_CEILING,
}: UseEstimatedProgressInput): number {
  const [estimated, setEstimated] = useState(0);
  const startedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isActive) {
      startedAtRef.current = null;
      setEstimated(0);
      return undefined;
    }

    startedAtRef.current = performance.now();
    setEstimated(0);

    const tick = (): void => {
      const startedAt = startedAtRef.current;
      if (startedAt === null) return;
      const elapsedSec = (performance.now() - startedAt) / 1000;
      const tau = Math.max(1, expectedSeconds) / 2;
      const fraction = 1 - Math.exp(-elapsedSec / tau);
      setEstimated(Math.min(ceiling, fraction * ceiling));
    };

    tick();
    const intervalId = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [isActive, expectedSeconds, ceiling]);

  if (!isActive) return 0;

  const backend = typeof backendProgress === 'number' ? backendProgress : null;
  if (backend !== null && backend > estimated) return backend;
  return estimated;
}
